import Image from 'next/image'
import React from 'react'

import { faHeadset } from '@fortawesome/pro-light-svg-icons/faHeadset'
import { Container, styled, Typography } from '@mui/material'

import { Button } from '@/components/Button/Button'
import { Icon } from '@/components/Icon/Icon'
import { IconLabel } from '@/components/IconLabel/IconLabel'
import { pxToRem } from '@/shared/styles/themes'
import { realTimeEditingData } from '@/slices/HomePage/mocks/realTimeEditing/data'

export const RealTimeEditing: React.FC = () => {
  const { headline, body, bulletPoints, gifUrl, ctaText, ctaLink, supportText } = realTimeEditingData

  return (
    <StyledSection>
      <Container maxWidth="lg">
        <StyledContainer>
          <StyledTextContent>
            <StyledTextContainer>
              <StyledHeadline variant="h2">
                {headline}
              </StyledHeadline>
              <StyledBodyText variant="body1">
                {body}
              </StyledBodyText>
              <StyledBulletList>
                {bulletPoints.map((point, index) => (
                  <IconLabel
                    key={index}
                    iconName={point.icon}
                    text={point.text}
                    direction="row"
                  />
                ))}
              </StyledBulletList>
            </StyledTextContainer>
            {ctaText && ctaLink && (
              <StyledCtaButton arrow primary size="large" href={ctaLink}>
                {ctaText}
              </StyledCtaButton>
            )}
            {supportText && (
              <StyledSupportText>
                <Icon icon={faHeadset} sx={{ fontSize: pxToRem(20) }} />
                <Typography variant="body2" color="text.secondary">
                  {supportText}
                </Typography>
              </StyledSupportText>
            )}
          </StyledTextContent>
          <StyledImageContainer>
            <StyledImageWrapper>
              <Image
                src={gifUrl}
                alt="Pixie World story editor showing a book being customized"
                fill
                sizes="(min-width: 900px) 560px, 100vw"
                style={{ objectFit: 'cover', borderRadius: '12px' }}
              />
              <StyledMascot>
                <Image
                  src="/images/pixie-regenerate.png"
                  alt="Pixie mascot waving a sparkle wand to regenerate a page"
                  fill
                  sizes="(min-width: 900px) 180px, 120px"
                  style={{ objectFit: 'contain' }}
                />
              </StyledMascot>
            </StyledImageWrapper>
          </StyledImageContainer>
        </StyledContainer>
      </Container>
    </StyledSection>
  )
}

const StyledTextContainer = styled('div')(({ theme }) => ({
  marginBottom: pxToRem(20),

  [theme.breakpoints.up('md')]: {
    marginBottom: pxToRem(25),
  },
}))

const StyledCtaButton = styled(Button)(({ theme }) => ({
  color: theme.palette.secondary.main,
  fontWeight: 500,
  padding: theme.spacing(2.5, 4),
}))

const StyledSection = styled('section')(({ theme }) => ({
  backgroundColor: '#edd8d8',
  padding: theme.spacing(5, 0),

  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(8, 0),
  },
}))

const StyledContainer = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(4),
  gridTemplateColumns: '1fr',
  alignItems: 'center',

  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(6),
  },
}))

const StyledTextContent = styled('div')(({ theme }) => ({
  textAlign: 'left',
  order: 2,

  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',

  [theme.breakpoints.up('md')]: {
    order: 1,
    alignItems: 'flex-start',
  },
}))

const StyledHeadline = styled(Typography)(({ theme }) => ({
  textAlign: 'left',
  color: theme.palette.primary.main,
  paddingBottom: pxToRem(10),
}))

const StyledBodyText = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontsize: pxToRem(10),
  lineHeight: 1.5,
  paddingBottom: pxToRem(10),
}))

const StyledBulletList = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(2),
}))

const StyledImageContainer = styled('div')(({ theme }) => ({
  order: 1,
  borderRadius: pxToRem(12),

  [theme.breakpoints.up('md')]: {
    order: 2,
  },
}))

const StyledImageWrapper = styled('div')({
  position: 'relative',
  width: '100%',
  aspectRatio: '4/3',
})

const StyledMascot = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: pxToRem(-32),
  left: pxToRem(-24),
  width: pxToRem(120),
  aspectRatio: '1/1',
  pointerEvents: 'none',
  filter: 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.12))',
  zIndex: 1,

  [theme.breakpoints.up('md')]: {
    top: pxToRem(-48),
    left: pxToRem(-40),
    width: pxToRem(180),
  },
}))

const StyledSupportText = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(2.5),
  color: theme.palette.text.secondary,
}))
